/**
 * Generate a dictionary with minified keys from a given object.
 * The minified keys are generated using the initials from original. Accepts camelCase and snake_case.
 * Obs: when a key has the same initials as another, the last character is repeated.
 * @param {Object} object - Object to generate dictionary
 * @param {Object} [options={ removeMissing: false, previousDictionary: {}, preserveKeys: string[] }]
 * @param {Object.<string, string>} [options.previousDictionary] - Previously generated dictionary to be incremented. It prevents overrides
 * @param {boolean} [options.removeMissing=false] - Remove keys not found in current object and is present in previousDictionary
 * @param {string[]} [options.preserveKeys=[]] - Keys to not be changed
 * @returns {Object.<string, string>} - Dictionary with minified keys
 */
const generateDictionaryFromKeys = (
  object,
  {
    previousDictionary = {},
    preserveKeys = [],
    removeMissing = false,
  } = {},
) => {
  const { dictionary, processedKeysMap } = processOldKeys(previousDictionary);
  const keysPresent = {};
  const preserveKeysMap = preserveKeys.reduce((accumulator, key) => {
    accumulator[key] = true;

    return accumulator;
  }, {});

  const processObject = localObject => Object.entries(localObject).forEach(([key, value]) => {
    if (removeMissing) {
      keysPresent[key] = true;
    }

    if (processedKeysMap[key]) {
      processEntity(value);
      return;
    }

    let minifiedKey;

    if (preserveKeysMap[key]) {
      minifiedKey = key;
    } else {
      minifiedKey = key
        .split(/(?:(?=[A-Z])|_)/)
        .filter(value => value)
        .map(str => str[0].toLowerCase())
        .join('');

      while (dictionary[minifiedKey]) {
        minifiedKey = `${minifiedKey}${minifiedKey[minifiedKey.length - 1]}`;
      }
    }

    dictionary[minifiedKey] = key;
    processedKeysMap[key] = true;

    processEntity(value);
  });

  const processEntity = (entity) => {
    if (Array.isArray(entity)) {
      entity.forEach(processEntity);
    } else if (typeof entity === 'object' && entity !== null) {
      processObject(entity);
    }
  };

  processObject(object);

  if (removeMissing) {
    return Object.entries(dictionary).reduce((accumulator, [key, value]) => {
      if (keysPresent[value]) {
        accumulator[key] = value;
      }

      return accumulator;
    }, {});
  }

  return sortObjectByKeys(dictionary);
};

const sortObjectByKeys = (object) => {
  return Object.entries(object).sort().reduce((accumulator, [key, value]) => {
    accumulator[key] = value;

    return accumulator;
  }, {});
};

/**
 * Load previous dictionary data to prevent overrides
 * @param {Object.<string, string>} previousDictionary
 * @returns {{dictionary, processedKeysMap: {}}}
 */
const processOldKeys = (previousDictionary) => {
  const dictionary = previousDictionary;
  const processedKeysMap = {};

  Object.values(previousDictionary).forEach(value => processedKeysMap[value] = true);

  return { dictionary, processedKeysMap };
};

module.exports = generateDictionaryFromKeys;
